import React, { Fragment, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { includes } from 'lodash-es';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import SettingsIcon from '@material-ui/icons/Settings';
import Input from '@material-ui/core/Input';
import { IconButton, Tooltip } from '@material-ui/core/';
import BlockIcon from '@material-ui/icons/Block';
import { Checkmark } from '@mummssoftware/icons';
import { ReactDatePicker, ReactDateTimePicker } from '@mummssoftware/common-ui';
import Close from '@material-ui/icons/Close';
import Check from '@material-ui/icons/Check';
import {
  formatDateYYYYMMDD,
  formatPostDate,
} from '@mummssoftware/utils/formatters';
import { Delete, Edit } from '@mummssoftware/utils/constants';
import { preventFutureTimes } from '@mummssoftware/utils/utils';
import { Context } from '../../../context';
import { MeasuresContext } from '../../MeasuresContext/MeasuresContextProvider';
import {
  patientRefused,
  noPost,
  active,
  SET_EDITING_MEASURE,
} from '../../MeasuresContext/utils/constants';
import { getMeasureHistoryDisplayValue } from '../../MeasuresContext/utils/utils';
import {
  CellDiv,
  MeasureValue,
  MeasureDescription,
  EnterCell,
  MeasureCell,
  MeasureCellScrollable,
  UnitsValue,
  PrintMeasureCell,
  InputContainer,
  CalendarDiv,
  borderlessButtonStyle,
} from '../../../components/layout/WrapperDiv';

import defaults from '../../../translations/en.json';

export const UnitsCellRenderer = React.memo((props: any) => {
  const [width] = useState(
    props.width ||
      props.agGridReact.props.columnDefs[2].cellRendererParams.width,
  );

  return props.value ? (
    <MeasureCell width={width}>
      <UnitsValue>{props.value}</UnitsValue>
    </MeasureCell>
  ) : (
    <EnterCell width={width}>Enter Unit</EnterCell>
  );
});

export const PrintUnitsCellRenderer = React.memo((props: any) => {
  const [width] = useState(
    props.width ||
      props.agGridReact.props.columnDefs[2].cellRendererParams.width,
  );

  return (
    <PrintMeasureCell width={width}>
      <UnitsValue>{props.value}</UnitsValue>
    </PrintMeasureCell>
  );
});

export const ValuesCellRenderer = React.memo((props: any) => {
  const [width] = useState(
    props.width ||
      props.agGridReact.props.columnDefs[3].cellRendererParams.width,
  );

  if (!props.value) {
    return <EnterCell width={width}>Enter Measure</EnterCell>;
  }

  const displayText = getMeasureHistoryDisplayValue({
    measuretypeshortname:
      props.data?.shortName || props.data?.measuretypeshortname,
    value: props.value.value,
    displayalue: props.value.displayvalue,
    missingreason: props.data?.missingreason,
    units: props.data?.units,
  } as mumms.measure);

  return (
    <MeasureCellScrollable width={width}>{displayText}</MeasureCellScrollable>
  );
});

export const ScoreCellRenderer = React.memo((props: any) => (
  <span>{props.value.value}</span>
));

export const MeasureCellRenderer = React.memo((props: any) => {
  const { data } = props;
  const result = props && props.value;
  // displayvalue keeps the formatted reading (e.g. BP "120/30"); value is the raw first number
  const value = result && (result.displayvalue || result.value);
  const keys = result && Object.keys(props.value);
  const isDisplay = includes(keys, 'value');
  const comments = result && result.comments;
  const getIcon = () => <BlockIcon style={{ fontSize: 15, marginLeft: 2 }} />;
  const { id } = props.data;
  const { editingMeasure } = useContext(MeasuresContext);
  const [measureValue, setMeasureValue] = useState(value);

  return (
    (editingMeasure && id === editingMeasure.id && (
      <InputContainer>
        <Input
          type="text"
          autoFocus
          onChange={(e) => {
            setMeasureValue(e.target.value);
            data.value = e.target.value;
          }}
          value={measureValue}
        />
      </InputContainer>
    )) ||
    (isDisplay && (
      <CellDiv>
        <span>{value}</span>
        {comments === patientRefused && (
          <Tooltip title={comments}>{getIcon()}</Tooltip>
        )}
      </CellDiv>
    )) || <span>{props.value}</span>
  );
});

export const PostCellRenderer = React.memo((props: any) => {
  const activeStyle = { cursor: 'pointer', color: '#9edbf7' };
  const disabledStyle = { color: 'grey' };
  const style = props.value !== active ? disabledStyle : activeStyle;

  const { recordSelected, saveMeasures } = props;
  const [recorded, setRecorded] = useState(false);
  return (
    <CellDiv>
      {!recorded && props.value !== noPost && (
        <Checkmark
          style={style}
          onClick={() => {
            const saved = saveMeasures(props.api, recordSelected);
            saved && setRecorded(true);
          }}
        />
      )}
    </CellDiv>
  );
});

export const RecordedByCellRenderer = ({ data }: any) => {
  const recordedByPinned = (user: mumms.User) => {
    if (user.firstName) {
      return user.userCredentials
        ? `${user.userCredentials} ${user.firstName.slice(0, 1)}. ${
            user.lastName
          }`
        : `${user.firstName.slice(0, 1)}. ${user.lastName}`;
    }
    return '';
  };
  const recordedBy = (user: mumms.measure) => {
    if (user.personfirstname) {
      return user.personcredentials
        ? `${user.personcredentials} ${user.personfirstname.slice(0, 1)}. ${
            user.personlastname
          }`
        : `${user.personfirstname.slice(0, 1)}. ${user.personlastname}`;
    }
    return '';
  };
  if (data.recordingUser) {
    const currentUser = data.recordingUser;
    return recordedByPinned(currentUser);
  }
  return recordedBy(data);
};

export const DateCellRenderer = React.memo((props: any) => {
  const { data } = props;
  const { id } = data;
  const patientDateConstraint = props && props.patientDateConstraint;

  const [vitalDate, setVitalDate] = useState(new Date());
  const [editingDate, setEditingDate] = useState(
    new Date(data.measuredatetimeshifted),
  );

  const { editingMeasure } = useContext(MeasuresContext);

  useEffect(() => {
    const editingDateMoment = moment(editingDate);

    if (editingDateMoment.isBefore(patientDateConstraint)) {
      setEditingDate(new Date());
    }
  }, []);

  const handleDatePickerChange = (date: any) => {
    if (!editingMeasure) {
      props.saveDate && props.saveDate(date);
      setVitalDate(date);
      return;
    }

    setEditingDate(date);
    data.measuredate = formatDateYYYYMMDD(date);
    data.measuredatetime = formatPostDate(date);
  };

  const dateConstraintDate = moment.isMoment(patientDateConstraint)
    ? patientDateConstraint.toDate()
    : patientDateConstraint;

  // data.date is passed down when the agrid table is rendered
  return (
    /* 
    if the measure is selected to edit,
    and THERE IS NO VISIT ASSOCIATED with it, render a date time picker to 
    make the measure's date and time editable
    */
    (data &&
      data.date &&
      editingMeasure &&
      id === editingMeasure.id &&
      !data.visitdate && (
        <CalendarDiv>
          <ReactDateTimePicker
            inputWidth="140px"
            selectedDate={editingDate}
            onChange={handleDatePickerChange}
            minimumDate={dateConstraintDate}
            maximumDate={new Date()}
            filterTime={preventFutureTimes}
            /* 
            'document' ties the popper's ability to move over/under the cursor to the scope of the elements that surround it

            'tether' set to false is so the popper isn't fixed (tethered) to where it was initiated (the popper will follow the user's scrolling etc within the scope it's in)
            */
            popperModifiers={[
              {
                name: 'preventOverflow',
                options: {
                  rootBoundary: 'document',
                  tether: false,
                },
              },
            ]}
          />
        </CalendarDiv>
      )) ||
    /* 
      if there IS a visit associated with the measure selected to edit, 
      make the date not editable, but show a tooltip when hovering, 
      that the measure is associated with a visit
    */
    (data &&
      data.date &&
      editingMeasure &&
      id === editingMeasure.id &&
      data.visitdate && (
        <Tooltip title={defaults.dateNotEditable}>
          <CellDiv>{data.date}</CellDiv>
        </Tooltip>
      )) ||
    // otherwise just show the measure's data for the table if it renders
    (data && data.date && <CellDiv>{data.date}</CellDiv>) || (
      <ReactDatePicker
        selectedDate={vitalDate}
        onChange={handleDatePickerChange}
        maximumDate={new Date()}
        minimumDate={dateConstraintDate}
      />
    )
  );
});

export const OptionsCellRenderer = (props: any) => {
  const { data, gridOptions } = props;
  const { id } = data;
  const { editingMeasure, dispatch } = useContext(MeasuresContext);
  const { handleMeasureChange, patient } = useContext(Context);
  const [disableButtons, setDisableButtons] = useState(false);

  const handleRowEditClose = () => {
    if (gridOptions.api) {
      gridOptions.api.redrawRows();
    }
    dispatch({
      type: SET_EDITING_MEASURE,
      payload: null,
    });
  };

  const handleRowEditSave = () => {
    setDisableButtons(true);
    if (patient) {
      // BP rows hold only the systolic in value; the upsert needs "sys/dia" to split
      const isUneditedBp =
        data.measuretypeshortname === 'bp' &&
        !String(data.value || '').includes('/') &&
        String(data.displayalue || '').includes('/');
      handleMeasureChange(
        patient.patientNumber,
        isUneditedBp ? { ...data, value: data.displayalue } : data,
        'PUT',
        data.measurecategoryshortname,
      )
        .then(() => {
          handleRowEditClose();
        })
        .catch(() => setDisableButtons(false));
    }
  };

  return (
    <div>
      {editingMeasure && id === editingMeasure.id && (
        <Fragment>
          <Tooltip title={defaults.save}>
            <IconButton
              size="small"
              style={borderlessButtonStyle}
              onClick={handleRowEditSave}
              disabled={disableButtons}
            >
              <Check />
            </IconButton>
          </Tooltip>
          <Tooltip title={defaults.close}>
            <IconButton
              onClick={handleRowEditClose}
              size="small"
              style={borderlessButtonStyle}
              disabled={disableButtons}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Fragment>
      )}
    </div>
  );
};

export const SettingsCellRenderer = (props: any) => {
  const { data } = props;
  const { id } = data;
  const { dispatch, editingMeasure } = useContext(MeasuresContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const cellOptions = [Edit, Delete];
  const { handleMeasureChange, patient } = useContext(Context);

  const selectOption = (option: string) => {
    if (option === Edit) {
      dispatch({
        type: SET_EDITING_MEASURE,
        payload: { ...data },
      });
    } else if (option === Delete && patient) {
      const deleteMeasure = { ...data };
      handleMeasureChange(
        patient.patientNumber,
        deleteMeasure,
        'DELETE',
        deleteMeasure.measurecategoryshortname,
      );
    }
    onClose();
  };

  const getMenuUI = () => (
    <div>
      {cellOptions.map((option: string) => (
        <MenuItem
          key={option}
          value={option}
          onClick={(e) => {
            selectOption(option);
          }}
        >
          {option}
        </MenuItem>
      ))}
    </div>
  );

  const onClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event: any) => {
    // stop propagation of panel expansion
    // event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <div>
      <IconButton
        onClick={handleEdit}
        size="small"
        disabled={!!(editingMeasure && id !== editingMeasure.id)}
      >
        <SettingsIcon />
      </IconButton>
      <Fragment>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
          {getMenuUI()}
        </Menu>
      </Fragment>
    </div>
  );
};

// not used
export const CommentsCellRenderer = (props: any) => {
  const { data } = props;
  const { id } = data;
  const { editingMeasure } = useContext(MeasuresContext);

  const [comments, setComments] = useState<string>(data.comments);

  return (
    <div>
      {(editingMeasure && id === editingMeasure.id && (
        <InputContainer>
          <Input
            type="text"
            autoFocus
            onChange={(e) => {
              setComments(e.target.value);
              data.comments = e.target.value;
            }}
            value={comments}
          />
        </InputContainer>
      )) || <span>{data.comments}</span>}
    </div>
  );
};
